import React, { JSX } from 'react';
import { IconButton } from '@material-ui/core';
import { startOfISOWeek, endOfISOWeek, isSameDay, isWithinInterval } from 'date-fns';
import styled, { css } from 'styled-components';
import { ColorDLS } from '../../../styling/color';

const DayIconButton = styled<any>(IconButton)`
  && {
    width: 36px;
    height: 36px;
    margin: 0 2px;
    padding: 0;
    font-size: 0.75rem;
    font-weight: 600;

    ${({ disabled }) =>
      disabled &&
      css`
        color: rgba(0, 0, 0, 0.38);
        font-weight: 400;
      `}

    ${({ hidden }) =>
      hidden &&
      css`
        visibility: hidden;
      `}

  ${({ selected }) =>
      selected &&
      css`
        color: white;
        background-color: ${ColorDLS.Blue45};
      `}

  ${({ outline }) =>
      outline &&
      css`
        box-shadow: 0 0 0 0.0625em rgb(255 255 255), 0 0 0 0.25em rgb(20 116 164 / 70%);
      `}
  }
`;

const StyledDay = styled.div<{
  highlight?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}>`
  margin: 0;

  ${({ highlight }) =>
    highlight &&
    css`
      background: ${ColorDLS.Blue10};
    `}

  ${({ isFirst }) =>
    isFirst &&
    css`
      border-top-left-radius: 50%;
      border-bottom-left-radius: 50%;
    `};

  ${({ isLast }) =>
    isLast &&
    css`
      border-top-right-radius: 50%;
      border-bottom-right-radius: 50%;
    `};
`;

interface DayProps {
  current?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  selected?: boolean;
  children?: JSX.Element;
}

export function renderWrappedWeekDay(
  date: Date,
  selectedDate: Date,
  dayInCurrentMonth: number,
  { props }: { props: DayProps },
) {
  let dateClone = new Date(date.getTime());
  let selectedDateClone = new Date(selectedDate.getTime());

  const start = startOfISOWeek(selectedDateClone);
  const end = endOfISOWeek(selectedDateClone);

  const dayIsBetween = isWithinInterval(dateClone, { start, end });
  const isFirstDay = isSameDay(dateClone, start);
  const isLastDay = isSameDay(dateClone, end);

  return (
    <StyledDay highlight={dayIsBetween} isFirst={isFirstDay} isLast={isLastDay}>
      <DayIconButton
        selected={isFirstDay || isLastDay}
        outline={false}
        disabled={props.disabled}
        hidden={props.hidden}
      >
        {props.children}
      </DayIconButton>
    </StyledDay>
  );
}

export function renderDay(
  date: Date,
  selectedDate: Date,
  dayInCurrentMonth: number,
  { props }: { props: DayProps },
) {
  return (
    <StyledDay>
      <DayIconButton
        selected={props.selected}
        outline={props.selected}
        disabled={props.disabled}
        hidden={props.hidden}
      >
        {props.children}
      </DayIconButton>
    </StyledDay>
  );
}
